// src/lib/webauthn/webauthn.ts
// Passkey ceremonies: identity's base64url JSON options in, the PublicKeyCredential.toJSON() shape out (contract §6.3, §6.8).

import type {
	AssertionCredentialJSON,
	AttestationCredentialJSON,
	PublicKeyCreationOptionsJSON,
	PublicKeyCredentialDescriptorJSON,
	PublicKeyRequestOptionsJSON,
} from "../../types/auth/mfa";

export const isPasskeySupported = (): boolean =>
	typeof window !== "undefined" &&
	typeof window.PublicKeyCredential !== "undefined" &&
	typeof navigator !== "undefined" &&
	typeof navigator.credentials?.get === "function";

export const base64urlToBuffer = (value: string): ArrayBuffer => {
	const base64 = value.replace(/-/g, "+").replace(/_/g, "/");
	const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
	const binary = atob(padded);
	const bytes = new Uint8Array(binary.length);
	for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
	return bytes.buffer;
};

export const bufferToBase64url = (buffer: ArrayBuffer | ArrayBufferView): string => {
	const bytes = buffer instanceof ArrayBuffer ? new Uint8Array(buffer) : new Uint8Array(buffer.buffer, buffer.byteOffset, buffer.byteLength);
	let binary = "";
	for (let i = 0; i < bytes.length; i += 1) binary += String.fromCharCode(bytes[i]);
	return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
};

const descriptor = (d: PublicKeyCredentialDescriptorJSON): PublicKeyCredentialDescriptor => ({
	type: d.type,
	id: base64urlToBuffer(d.id),
	...(d.transports ? { transports: d.transports as AuthenticatorTransport[] } : {}),
});

export const toRequestOptions = (json: PublicKeyRequestOptionsJSON): PublicKeyCredentialRequestOptions => ({
	...json,
	challenge: base64urlToBuffer(json.challenge),
	allowCredentials: (json.allowCredentials ?? []).map(descriptor),
});

export const toCreationOptions = (json: PublicKeyCreationOptionsJSON): PublicKeyCredentialCreationOptions => ({
	...json,
	challenge: base64urlToBuffer(json.challenge),
	user: { ...json.user, id: base64urlToBuffer(json.user.id) },
	excludeCredentials: (json.excludeCredentials ?? []).map(descriptor),
});

export type PasskeyFailure = "cancelled" | "already_registered" | "unsupported" | "failed";

// Why a ceremony ended without a credential, in the terms the UI words its message by.
export class PasskeyCeremonyError extends Error {
	constructor(public readonly reason: PasskeyFailure) {
		super(`passkey_${reason}`);
		this.name = "PasskeyCeremonyError";
	}
}

const ceremonyError = (error: unknown): PasskeyCeremonyError => {
	const name = (error as { name?: string })?.name;
	// The browser reports a dismissed prompt and a timeout identically, on purpose.
	if (name === "NotAllowedError" || name === "AbortError") return new PasskeyCeremonyError("cancelled");
	if (name === "InvalidStateError") return new PasskeyCeremonyError("already_registered");
	if (name === "NotSupportedError" || name === "SecurityError") return new PasskeyCeremonyError("unsupported");
	return new PasskeyCeremonyError("failed");
};

export const getPasskeyAssertion = async (options: PublicKeyRequestOptionsJSON): Promise<AssertionCredentialJSON> => {
	if (!isPasskeySupported()) throw new PasskeyCeremonyError("unsupported");
	let credential: Credential | null;
	try {
		credential = await navigator.credentials.get({ publicKey: toRequestOptions(options) });
	} catch (error) {
		throw ceremonyError(error);
	}
	if (!credential) throw new PasskeyCeremonyError("cancelled");
	const pk = credential as PublicKeyCredential;
	const response = pk.response as AuthenticatorAssertionResponse;
	return {
		id: pk.id,
		rawId: bufferToBase64url(pk.rawId),
		type: "public-key",
		response: {
			clientDataJSON: bufferToBase64url(response.clientDataJSON),
			authenticatorData: bufferToBase64url(response.authenticatorData),
			signature: bufferToBase64url(response.signature),
			...(response.userHandle ? { userHandle: bufferToBase64url(response.userHandle) } : {}),
		},
	};
};

export const createPasskeyCredential = async (options: PublicKeyCreationOptionsJSON): Promise<AttestationCredentialJSON> => {
	if (!isPasskeySupported() || typeof navigator.credentials.create !== "function") throw new PasskeyCeremonyError("unsupported");
	let credential: Credential | null;
	try {
		credential = await navigator.credentials.create({ publicKey: toCreationOptions(options) });
	} catch (error) {
		throw ceremonyError(error);
	}
	if (!credential) throw new PasskeyCeremonyError("cancelled");
	const pk = credential as PublicKeyCredential;
	const response = pk.response as AuthenticatorAttestationResponse;
	const transports = typeof response.getTransports === "function" ? response.getTransports() : undefined;
	return {
		id: pk.id,
		rawId: bufferToBase64url(pk.rawId),
		type: "public-key",
		response: {
			clientDataJSON: bufferToBase64url(response.clientDataJSON),
			attestationObject: bufferToBase64url(response.attestationObject),
			...(transports && transports.length > 0 ? { transports } : {}),
		},
	};
};

export const passkeyFailureMessage = (reason: PasskeyFailure): string => {
	switch (reason) {
		case "cancelled":
			return "The passkey request was cancelled or timed out. Try again.";
		case "already_registered":
			return "This device already holds a passkey for your account.";
		case "unsupported":
			return "This browser or device cannot use passkeys.";
		default:
			return "The passkey could not be used. Try again.";
	}
};

// A starting name the user can change; the platform guess is only a hint.
export const suggestedPasskeyName = (): string => {
	const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
	if (/iPhone/i.test(ua)) return "iPhone";
	if (/iPad/i.test(ua)) return "iPad";
	if (/Android/i.test(ua)) return "Android device";
	if (/Macintosh|Mac OS X/i.test(ua)) return "Mac";
	if (/Windows/i.test(ua)) return "Windows device";
	return "Passkey";
};
